/**
 * SettlementInfoSection Component
 * 협력사 정산 정보 입력/수정 섹션
 */
import React, { useState, useEffect, useRef } from 'react';
import { SupplierInfo } from '../types/supplierInfo.types';
import { SaveIcon } from '@/shared/components/icons/Icons';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';
import { sanitizeAccountHolder, sanitizeAccountNumber } from '../lib/inputValidationHelpers';

interface SettlementInfoSectionProps {
    supplierInfo: SupplierInfo;
    saving: boolean;
    onFieldChange: (field: keyof SupplierInfo | Partial<SupplierInfo>, value?: string) => void;
    onSave: () => void;
}

const SettlementInfoSection: React.FC<SettlementInfoSectionProps> = ({
    supplierInfo,
    saving,
    onFieldChange,
    onSave
}) => {
    const [bankCodes, setBankCodes] = useState<CommonCodeDto[]>([]);
    const hasFetchedBankCodes = useRef(false);

    // 은행 코드 목록 조회
    useEffect(() => {
        if (hasFetchedBankCodes.current) return;

        let isCancelled = false;

        const fetchBankCodes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('BANK_CODE');
                if (!isCancelled) {
                    setBankCodes(response.codes || []);
                    hasFetchedBankCodes.current = true;
                }
            } catch {
                // 은행 코드 조회 실패 시 기본값 유지
            }
        };

        fetchBankCodes();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 현재 선택된 은행 코드에 해당하는 은행명 찾기
    const selectedBank = bankCodes.find(bank => bank.comCode === supplierInfo.bankCode);
    const selectedBankName = selectedBank?.comCdName || supplierInfo.bankName || '';

    // 은행 선택 핸들러 - bankCode와 bankName을 한 번에 업데이트
    const handleBankChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedBank = bankCodes.find(bank => bank.comCdName === e.target.value);
        if (selectedBank) {
            // bankCode와 bankName을 한 번에 업데이트
            onFieldChange({
                bankCode: selectedBank.comCode,
                bankName: selectedBank.comCdName
            } as Partial<SupplierInfo>);
        }
    };

    return (
        <div className="supplier-section">
            <h2 className="section-title">정산 정보</h2>
            <div className="supplier-form-grid">
                {/* 은행명 */}
                <div className="form-group">
                    <label className="form-label">은행명:</label>
                    <select
                        className="form-select"
                        value={selectedBankName}
                        onChange={handleBankChange}
                    >
                        <option value="">선택하세요</option>
                        {bankCodes.map(bank => (
                            <option key={bank.comCode} value={bank.comCdName}>
                                {bank.comCdName}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 계좌번호 */}
                <div className="form-group">
                    <label className="form-label">계좌번호:</label>
                    <input
                        type="text"
                        className="form-input"
                        value={supplierInfo.accountNumber}
                        onChange={(e) => {
                            const sanitizedValue = sanitizeAccountNumber(e.target.value);
                            onFieldChange('accountNumber', sanitizedValue);
                        }}
                    />
                </div>

                {/* 예금주 */}
                <div className="form-group">
                    <label className="form-label">예금주:</label>
                    <input
                        type="text"
                        className="form-input"
                        value={supplierInfo.accountHolder}
                        onChange={(e) => {
                            const sanitizedValue = sanitizeAccountHolder(e.target.value);
                            onFieldChange('accountHolder', sanitizedValue);
                        }}
                    />
                </div>

                {/* 저장 버튼 */}
                <div className="save-button-wrapper">
                    <label className="form-label" style={{ visibility: 'hidden' }}>.</label>
                    <button
                        className="btn btn-primary"
                        onClick={onSave}
                        disabled={saving}
                    >
                        <SaveIcon width={16} height={16} />
                        <span>{saving ? '저장 중...' : '저장'}</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SettlementInfoSection;
